import { useCallback, useRef } from 'react';
import * as Sentry from "@sentry/react";

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface AnalyticsConfig {
  user_id?: string;
  send_page_view?: boolean;
  [key: string]: any;
}

/**
 * Hook for Google Analytics tracking
 * @param config Optional configuration for analytics operations
 */
const useAnalytics = (config?: AnalyticsConfig) => {
  const isInitialized = useRef(false);

  /**
   * Initialize Google Analytics
   * @param tag Google Analytics measurement ID
   */
  const initAnalytics = useCallback(
    (tag: string) => {
      return new Promise<void>((resolve, reject) => {
        if (isInitialized.current) {
          console.warn('Google Analytics is already initialized.');
          return resolve();
        }

        const analyticsConfig = config || {};

        try {
          const script = document.createElement('script');
          script.async = true;
          script.src = `https://www.googletagmanager.com/gtag/js?id=${tag}`;
          script.onerror = (error) => {
            console.warn('Failed to load Google Analytics script:', error);
            reject();
          };
          document.head.appendChild(script);

          window.dataLayer = window.dataLayer || [];
          // eslint-disable-next-line
          function gtag() {
            window.dataLayer.push(arguments);
          }
          window.gtag = gtag;

          script.onload = () => {
            try {
              window.gtag('js', new Date());
              
              window.gtag('config', tag, analyticsConfig);
              isInitialized.current = true;
              resolve();
            } catch (error) {
              console.error('GA script loaded, but initialization failed:', error);
              reject(error);
            }
          };
        } catch (error) {
          console.error('Unexpected error while initializing Google Analytics:', error);
          reject(error);
        }
      });
    },
    [config]
  );

  /**
   * Send an analytics event
   * @param eventName Name of the event to send
   * @param eventParams Additional parameters for the event
   */
  const sendEvent = useCallback(
    (eventName: string, eventParams: Record<string, any> = {}) => {
      // Check if gtag is available globally (may have been initialized by another hook instance)
      if (typeof window.gtag !== 'function') {
        console.warn('Google Analytics is not initialized. Call initAnalytics() first.');
        return;
      }

      try {
        const params = { ...eventParams };
        
        // Add user_id from config if available and not already in params
        if (config?.user_id && !params.user_id) {
          params.user_id = config.user_id;
        }

        window.gtag('event', eventName, params);
      } catch (error) {
        console.error(`Failed to send GA event "${eventName}":`, error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        Sentry.captureException(new Error(`GA event failure: ${eventName} - ${errorMessage}`));
      }
    },
    [config]
  );

  return { initAnalytics, sendEvent };
};

export default useAnalytics;

